import React, { useState, useMemo } from 'react';
import { CircleDot } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { LengthInput } from '../../components/calculators/MeasureInputs';
import { GenderField } from '../../components/calculators/FormFields';
import UnitToggle from '../../components/calculators/UnitToggle';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { calculateWHR, getWHRRisk } from '../../lib/calculatorUtils';

const WHRCalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [gender, setGender] = useState('female');
  const [waistCm, setWaistCm] = useState('');
  const [hipCm, setHipCm] = useState('');

  const result = useMemo(() => {
    const w = parseFloat(waistCm);
    const h = parseFloat(hipCm);
    if (!w || !h || h <= 0) return null;
    const whr = calculateWHR(w, h);
    return { whr, risk: getWHRRisk(whr, gender) };
  }, [waistCm, hipCm, gender]);

  const inputs = (
    <div className="space-y-5" data-testid="whr-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Your Measurements</h2>
      <p className="text-slate-400 text-sm mb-5">Measure waist at the navel and hip at the widest point.</p>
      <GenderField value={gender} onChange={setGender} testIdPrefix="whr-gender" />
      <div className="flex items-center justify-between">
        <span className="block text-sm font-medium text-slate-300">Measurement Unit</span>
        <UnitToggle value={unit} onChange={setUnit} options={[{ value: 'metric', label: 'cm' }, { value: 'imperial', label: 'in' }]} label="Measurement unit" testIdPrefix="whr-unit" />
      </div>
      <LengthInput id="whr-waist" label="Waist" unit={unit} valueCm={waistCm} onValueCmChange={setWaistCm} testId="whr-waist-input" />
      <LengthInput id="whr-hip" label="Hip" unit={unit} valueCm={hipCm} onValueCmChange={setHipCm} testId="whr-hip-input" />
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="Waist-to-Hip Ratio" value={result.whr} accent="from-rose-500 to-orange-500" category={result.risk} testId="whr-result-value" />
      <div className="mt-8 space-y-1">
        <StatRow label="Risk category" value={result.risk.label} color={result.risk.color} testId="whr-risk" />
        <StatRow label="Low risk (male)" value="< 0.90" />
        <StatRow label="Low risk (female)" value="< 0.80" />
        <StatRow label="High risk (male)" value="> 1.00" />
        <StatRow label="High risk (female)" value="> 0.85" />
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        WHR is one of several markers used to estimate metabolic and cardiovascular risk. It should be interpreted alongside other clinical measures.
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter your waist and hip measurements to see your ratio and risk category." />
  );

  return (
    <CalculatorLayout
      slug="waist-to-hip" title="Waist-to-Hip Ratio Calculator"
      subtitle="Measure fat distribution and metabolic risk with the waist-to-hip ratio (WHR)."
      metaDescription="Waist-to-Hip Ratio Calculator to assess fat distribution and metabolic risk in seconds."
      icon={CircleDot}
      accentGradient="from-rose-500 to-orange-500" accentColor="text-rose-400"
      badgeBg="bg-rose-500/10" badgeBorder="border-rose-500/30"
      inputs={inputs} result={resultNode}
      formulaText={<><p><strong className="text-slate-300">Formula:</strong> WHR = Waist ÷ Hip</p></>}
    />
  );
};

export default WHRCalculatorPage;
